/**
 * Server-relayed STT — MediaRecorder → POST /api/aria/transcribe.
 * No browser Web Speech. Never auto-send.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { transcribeAriaAudio } from '../api/ariaChat'

export const SPEECH_SILENCE_MS = 8000

type Opts = {
  silenceMs?: number
  onFinalText: (text: string) => void
  onInterim: (text: string) => void
  onListeningChange: (listening: boolean) => void
  onCostEstimate?: (usd: number) => void
  onError?: (message: string) => void
}

function mediaOk(): boolean {
  return typeof window !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined'
}

export function useSpeechDictation({
  silenceMs = SPEECH_SILENCE_MS,
  onFinalText,
  onInterim,
  onListeningChange,
  onCostEstimate,
  onError,
}: Opts) {
  const [listening, setListening] = useState(false)
  const [speechOk] = useState(() => mediaOk())
  const [transcribing, setTranscribing] = useState(false)

  const wantRef = useRef(false)
  const baseRef = useRef('')
  const streamRef = useRef<MediaStream | null>(null)
  const recRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const startedAtRef = useRef(0)
  const silenceTimerRef = useRef<number | null>(null)
  const lastAudioRef = useRef(0)

  const onFinalTextRef = useRef(onFinalText)
  const onInterimRef = useRef(onInterim)
  const onListeningChangeRef = useRef(onListeningChange)
  const onCostRef = useRef(onCostEstimate)
  const onErrorRef = useRef(onError)
  onFinalTextRef.current = onFinalText
  onInterimRef.current = onInterim
  onListeningChangeRef.current = onListeningChange
  onCostRef.current = onCostEstimate
  onErrorRef.current = onError

  const clearSilence = useCallback(() => {
    if (silenceTimerRef.current != null) {
      window.clearInterval(silenceTimerRef.current)
      silenceTimerRef.current = null
    }
  }, [])

  const setListen = useCallback((v: boolean) => {
    setListening(v)
    onListeningChangeRef.current(v)
  }, [])

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    recRef.current = null
    chunksRef.current = []
  }, [])

  const finishBlob = useCallback(async (): Promise<string> => {
    const chunks = chunksRef.current
    const started = startedAtRef.current
    stopTracks()
    if (!chunks.length) return baseRef.current.trim()

    setTranscribing(true)
    onInterimRef.current('Transcribing…')
    try {
      const blob = new Blob(chunks, { type: chunks[0]?.type || 'audio/webm' })
      const durationSec = Math.max(0.5, (Date.now() - started) / 1000)
      const out = await transcribeAriaAudio(blob, durationSec)
      if (out.cost_estimate?.usd != null) onCostRef.current?.(out.cost_estimate.usd)
      const piece = (out.text || '').trim()
      const merged = `${baseRef.current}${piece}`.replace(/\s+/g, ' ').trim()
      baseRef.current = merged ? `${merged} ` : ''
      onFinalTextRef.current(merged)
      onInterimRef.current('')
      return merged
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Transcription failed'
      onErrorRef.current?.(msg)
      onInterimRef.current('')
      return baseRef.current.trim()
    } finally {
      setTranscribing(false)
    }
  }, [stopTracks])

  const clearAll = useCallback(() => {
    wantRef.current = false
    clearSilence()
    try { recRef.current?.stop() } catch { /* ignore */ }
    stopTracks()
    baseRef.current = ''
    onFinalTextRef.current('')
    onInterimRef.current('')
    setListen(false)
    setTranscribing(false)
  }, [clearSilence, setListen, stopTracks])

  const stopListening = useCallback(async () => {
    if (!wantRef.current && !recRef.current) {
      setListen(false)
      return baseRef.current.trim()
    }
    return new Promise<string>((resolve) => {
      const rec = recRef.current
      wantRef.current = false
      clearSilence()
      if (!rec || rec.state === 'inactive') {
        setListen(false)
        void finishBlob().then(resolve)
        return
      }
      rec.onstop = () => {
        setListen(false)
        void finishBlob().then(resolve)
      }
      try { rec.stop() } catch {
        setListen(false)
        void finishBlob().then(resolve)
      }
    })
  }, [clearSilence, finishBlob, setListen])

  const flushAndStop = useCallback(async () => stopListening(), [stopListening])

  const startListening = useCallback(async (currentText: string) => {
    if (!mediaOk()) return
    baseRef.current = currentText && !/\s$/.test(currentText) ? `${currentText} ` : (currentText || '')
    chunksRef.current = []
    wantRef.current = true
    lastAudioRef.current = Date.now()
    startedAtRef.current = Date.now()
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!wantRef.current) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }
      streamRef.current = stream
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm'
      const rec = new MediaRecorder(stream, { mimeType: mime })
      recRef.current = rec
      rec.ondataavailable = (ev) => {
        if (ev.data?.size) {
          chunksRef.current.push(ev.data)
          lastAudioRef.current = Date.now()
        }
      }
      rec.onstop = () => { /* handled by stopListening */ }
      rec.start(400)
      setListen(true)
      onInterimRef.current('')
      clearSilence()
      silenceTimerRef.current = window.setInterval(() => {
        if (!wantRef.current) return
        if (Date.now() - lastAudioRef.current >= silenceMs) {
          void stopListening()
        }
      }, 400)
    } catch (e) {
      wantRef.current = false
      setListen(false)
      const msg = e instanceof Error ? e.message : 'Mic unavailable'
      onErrorRef.current?.(msg)
    }
  }, [clearSilence, setListen, silenceMs, stopListening])

  useEffect(() => () => {
    wantRef.current = false
    clearSilence()
    try { recRef.current?.stop() } catch { /* ignore */ }
    stopTracks()
  }, [clearSilence, stopTracks])

  const toggle = useCallback((currentText: string) => {
    if (listening || transcribing) void stopListening()
    else void startListening(currentText)
  }, [listening, transcribing, startListening, stopListening])

  return {
    listening: listening || transcribing,
    transcribing,
    toggle,
    flushAndStop,
    clearAll,
    speechOk,
  }
}
